/**
 * Golden Gate shared-pool migration breadcrumbs: migration stamps `custom:oldSub`, and the
 * role-assignment worker ends by writing `custom:authzRekey = COMPLETE:<instant>` (or
 * `FAILED:<category>:<instant>`). Both are client-readable, so they appear in ID-token claims
 * and in /oauth2/userInfo. `oldSub` with no terminal marker means the re-key is not finished —
 * the only state the account-setup gate waits on. It may not progress without a new token
 * issuance: the trigger re-enqueues the user on every token issuance while the marker is unset,
 * and a run that failed terminally leaves no marker (only the trigger writes FAILED:, at the
 * next issuance). Anything unreadable maps to a non-waiting state: the gate must never strand a
 * user the backend would let in.
 */

export type RekeyMarkerState = 'ABSENT' | 'COMPLETE' | 'FAILED' | 'UNRECOGNIZED'

export const AUTHZ_REKEY_ATTRIBUTE = 'custom:authzRekey'
export const OLD_SUB_ATTRIBUTE = 'custom:oldSub'

/**
 * Only the two prefixes the backend writers produce are recognized; anything else is
 * UNRECOGNIZED so a marker-contract change surfaces in metrics instead of parking users.
 */
export function parseRekeyMarker (rawValue: unknown): RekeyMarkerState {
    if (typeof rawValue !== 'string' || rawValue.trim() === '') {
        return 'ABSENT'
    }
    if (rawValue.startsWith('COMPLETE:')) {
        return 'COMPLETE'
    }
    if (rawValue.startsWith('FAILED:')) {
        return 'FAILED'
    }
    return 'UNRECOGNIZED'
}

export type RekeySignal = 'NOT_MIGRATED' | 'PENDING' | 'COMPLETE' | 'FAILED' | 'UNRECOGNIZED'

export function rekeySignalFromClaims (claims: Record<string, unknown>): RekeySignal {
    const marker = parseRekeyMarker(claims[AUTHZ_REKEY_ATTRIBUTE])
    if (marker !== 'ABSENT') {
        return marker
    }
    const oldSub = claims[OLD_SUB_ATTRIBUTE]
    if (typeof oldSub === 'string' && oldSub.trim() !== '') {
        return 'PENDING'
    }
    return 'NOT_MIGRATED'
}
